<template>
  <div class="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
    <!-- Header avec logo -->
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <div class="flex justify-center">
        <div
          class="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg">
          <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
      </div>
      <h1 class="mt-4 text-center text-xl font-semibold text-gray-900">
        Validation Email
      </h1>
    </div>

    <!-- Contenu principal -->
    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div class="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10 border border-gray-200">
        
        <!-- État de chargement -->
        <div v-if="isLoading" class="text-center">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p class="mt-2 text-sm text-gray-600">Validation en cours...</p>
        </div>

        <!-- État de succès -->
        <div v-else-if="isSuccess" class="text-center">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
            <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 class="mt-2 text-sm font-medium text-gray-900">Email validé avec succès !</h3>
          <p class="mt-1 text-sm text-gray-500">
            Votre compte a été validé. Vous pouvez maintenant vous connecter.
          </p>
          <div class="mt-6">
            <button
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              @click="goToLogin"
            >
              Aller à la connexion
            </button>
          </div>
        </div>

        <!-- État d'erreur -->
        <div v-else-if="error" class="text-center">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h3 class="mt-2 text-sm font-medium text-gray-900">Erreur de validation</h3>
          <p class="mt-1 text-sm text-gray-500">{{ error }}</p>
          <div class="mt-6 space-y-3">
            <button
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              @click="retryValidation"
            >
              Réessayer
            </button>
            <button
              class="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              @click="goToLogin"
            >
              Aller à la connexion
            </button>
          </div>
        </div>

        <!-- État initial (token manquant) -->
        <div v-else class="text-center">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
            <svg class="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 class="mt-2 text-sm font-medium text-gray-900">Token de validation manquant</h3>
          <p class="mt-1 text-sm text-gray-500">
            Aucun token de validation n'a été trouvé dans l'URL.
          </p>
          <div class="mt-6">
            <button
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              @click="goToLogin"
            >
              Aller à la connexion
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { authService } from '@/services/authService';
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();

// État local
const isLoading = ref(false);
const isSuccess = ref(false);
const error = ref<string | null>(null);

// Récupérer le token depuis l'URL
const token = route.query.token as string;

// Validation du token
const validateToken = async (): Promise<void> => {
  if (!token) {
    error.value = 'Aucun token de validation fourni';
    return;
  }

  isLoading.value = true;
  error.value = null;

  try {
    await authService.validateEmail(token);
    isSuccess.value = true;
  } catch (err: any) {
    error.value = err.message || 'Erreur lors de la validation du token';
  } finally {
    isLoading.value = false;
  }
};

// Réessayer la validation
const retryValidation = (): void => {
  validateToken();
};

// Aller à la page de connexion
const goToLogin = (): void => {
  router.push({ name: 'auth', query: { mode: 'login' } });
};

// Initialisation
onMounted(() => {
  if (token) {
    validateToken();
  }
});
</script> 